/**
 * ---------------------------------------------------------------------------
 * VENNER — Reproductor de la sección "Video + características"
 * ---------------------------------------------------------------------------
 *
 * Un solo custom element (<venner-video-player>) que maneja tres orígenes de
 * video con la misma interfaz: archivo subido a Shopify, YouTube y Vimeo.
 *
 * Por qué no reutiliza <video-media> de Prestige: ese componente muestra los
 * controles nativos del proveedor (la barra de YouTube con su logo, el botón
 * de "ver más tarde", etc.) y no expone el tiempo de reproducción, que aquí se
 * necesita para pintar la barra de progreso propia.
 *
 * YouTube y Vimeo se controlan por postMessage contra el iframe, sin cargar el
 * SDK de ninguno de los dos.
 */

{
  const YT_ORIGIN = 'https://www.youtube.com';
  const VIMEO_ORIGIN = 'https://player.vimeo.com';

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
    const total = Math.floor(seconds);
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  class VennerVideoPlayer extends HTMLElement {
    #abortController;
    #observer;
    #provider = 'native';
    #duration = 0;
    #currentTime = 0;
    #isPlaying = false;
    #isScrubbing = false;
    #isMuted = true;
    #framePrepared = false;
    #frameReady = false;
    #pendingPlay = false;

    connectedCallback() {
      this.#abortController = new AbortController();
      this.#provider = this.getAttribute('provider') || 'native';
      this.#isMuted = this.hasAttribute('muted');

      this.video = this.querySelector('video');
      this.frame = this.querySelector('iframe');
      this.poster = this.querySelector('[data-vvf-poster]');
      this.range = this.querySelector('[data-vvf-range]');
      this.elapsedEl = this.querySelector('[data-vvf-elapsed]');
      this.durationEl = this.querySelector('[data-vvf-duration]');

      this.#wireControls();

      if (this.#provider === 'native') {
        this.#wireNative();
      } else {
        this.#wireFrame();
      }

      this.#observe();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#observer?.disconnect();
    }

    get autoplayInView() {
      return this.hasAttribute('autoplay-in-view');
    }

    get pauseOutOfView() {
      return !this.hasAttribute('keep-playing');
    }

    /**
     * -----------------------------------------------------------------------
     * API PÚBLICA
     * -----------------------------------------------------------------------
     */

    play() {
      this.#prepareFrame();

      if (this.#provider === 'native') {
        this.video?.play().catch(() => {
          /* el navegador bloqueó el autoplay: se queda en pausa */
        });
        return;
      }

      // El iframe se carga hasta que la sección entra en pantalla, así que la
      // primera orden de play puede llegar antes de que exista con quién
      // hablar. En ese caso se guarda y se manda en cuanto termina de cargar.
      if (!this.#frameReady) {
        this.#pendingPlay = true;
        return;
      }

      this.#command('play');
    }

    pause() {
      this.#pendingPlay = false;

      if (this.#provider === 'native') {
        this.video?.pause();
      } else if (this.#frameReady) {
        this.#command('pause');
      }
    }

    toggle() {
      this.#isPlaying ? this.pause() : this.play();
    }

    seekToFraction(fraction) {
      if (!this.#duration) return;
      const target = Math.max(0, Math.min(1, fraction)) * this.#duration;

      if (this.#provider === 'native') {
        if (this.video) this.video.currentTime = target;
      } else if (this.#provider === 'youtube') {
        this.#post({ event: 'command', func: 'seekTo', args: [target, true] });
      } else {
        this.#post({ method: 'setCurrentTime', value: target });
      }

      this.#currentTime = target;
      this.#renderProgress();
    }

    setMuted(muted) {
      this.#isMuted = muted;

      if (this.#provider === 'native') {
        if (this.video) this.video.muted = muted;
      } else if (this.#provider === 'youtube') {
        this.#post({ event: 'command', func: muted ? 'mute' : 'unMute', args: [] });
      } else {
        this.#post({ method: 'setVolume', value: muted ? 0 : 1 });
      }

      this.toggleAttribute('muted', muted);
      this.#syncMuteButtons();
    }

    /**
     * -----------------------------------------------------------------------
     * REPRODUCIR AL ENTRAR EN PANTALLA
     * -----------------------------------------------------------------------
     */

    #observe() {
      // Aunque no haya autoplay, observamos para pausar al salir de pantalla y
      // para no cargar el iframe hasta que haga falta.
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this.#prepareFrame();
              if (this.autoplayInView) this.play();
            } else if (this.pauseOutOfView && this.#isPlaying) {
              this.pause();
            }
          }
        },
        { threshold: 0.4 }
      );

      this.#observer.observe(this);
    }

    /**
     * -----------------------------------------------------------------------
     * CONTROLES
     * -----------------------------------------------------------------------
     */

    #wireControls() {
      const { signal } = this.#abortController;

      this.querySelectorAll('[data-vvf-toggle]').forEach((button) => {
        button.addEventListener('click', () => this.toggle(), { signal });
      });

      this.querySelectorAll('[data-vvf-mute]').forEach((button) => {
        button.addEventListener('click', () => this.setMuted(!this.#isMuted), { signal });
      });

      this.querySelectorAll('[data-vvf-fullscreen]').forEach((button) => {
        button.addEventListener('click', () => this.#requestFullscreen(), { signal });
      });

      if (this.range) {
        this.range.addEventListener('input', () => {
          this.#isScrubbing = true;
          this.#renderTime((Number(this.range.value) / 1000) * this.#duration);
        }, { signal });

        this.range.addEventListener('change', () => {
          this.#isScrubbing = false;
          this.seekToFraction(Number(this.range.value) / 1000);
        }, { signal });
      }

      this.#syncMuteButtons();
    }

    #requestFullscreen() {
      const target = this.querySelector('[data-vvf-stage]') || this;
      if (document.fullscreenElement) {
        document.exitFullscreen?.();
      } else {
        target.requestFullscreen?.();
      }
    }

    #syncMuteButtons() {
      this.querySelectorAll('[data-vvf-mute]').forEach((button) => {
        button.setAttribute('aria-pressed', String(this.#isMuted));
      });
    }

    #setPlaying(isPlaying) {
      this.#isPlaying = isPlaying;
      this.toggleAttribute('playing', isPlaying);

      if (isPlaying && this.poster) {
        this.poster.hidden = true;
      }

      this.querySelectorAll('[data-vvf-toggle]').forEach((button) => {
        button.setAttribute('aria-pressed', String(isPlaying));
      });
    }

    #renderTime(seconds) {
      if (this.elapsedEl) this.elapsedEl.textContent = formatTime(seconds);
      if (this.durationEl) this.durationEl.textContent = formatTime(this.#duration);
    }

    #renderProgress() {
      if (this.range && !this.#isScrubbing) {
        const fraction = this.#duration ? this.#currentTime / this.#duration : 0;
        this.range.value = String(Math.round(fraction * 1000));
        this.range.style.setProperty('--vvf-progress', `${fraction * 100}%`);
      }

      if (!this.#isScrubbing) this.#renderTime(this.#currentTime);
    }

    /**
     * -----------------------------------------------------------------------
     * VIDEO SUBIDO A SHOPIFY
     * -----------------------------------------------------------------------
     */

    #wireNative() {
      if (!this.video) return;
      const { signal } = this.#abortController;

      this.video.muted = this.#isMuted;

      this.video.addEventListener('loadedmetadata', () => {
        this.#duration = this.video.duration || 0;
        this.#renderProgress();
      }, { signal });

      this.video.addEventListener('timeupdate', () => {
        this.#currentTime = this.video.currentTime || 0;
        this.#duration = this.video.duration || this.#duration;
        this.#renderProgress();
      }, { signal });

      this.video.addEventListener('play', () => this.#setPlaying(true), { signal });
      this.video.addEventListener('pause', () => this.#setPlaying(false), { signal });
      this.video.addEventListener('ended', () => this.#setPlaying(false), { signal });
    }

    /**
     * -----------------------------------------------------------------------
     * YOUTUBE / VIMEO (por postMessage, sin cargar sus SDK)
     * -----------------------------------------------------------------------
     */

    #prepareFrame() {
      if (this.#framePrepared || !this.frame) return;
      this.#framePrepared = true;

      const src = this.frame.getAttribute('data-src');
      if (src) this.frame.setAttribute('src', src);
    }

    #wireFrame() {
      if (!this.frame) return;
      const { signal } = this.#abortController;

      this.frame.addEventListener('load', () => {
        this.#frameReady = true;
        this.#handshake();

        if (this.#pendingPlay) {
          this.#pendingPlay = false;
          this.#command('play');
        }
      }, { signal });

      window.addEventListener('message', (event) => {
        if (event.source !== this.frame.contentWindow) return;
        this.#onFrameMessage(event);
      }, { signal });
    }

    #handshake() {
      if (this.#provider === 'youtube') {
        this.#post({ event: 'listening', id: this.id || 'venner-video' });
      } else {
        ['timeupdate', 'play', 'pause', 'ended', 'loaded'].forEach((name) => {
          this.#post({ method: 'addEventListener', value: name });
        });
      }
    }

    #post(payload) {
      const origin = this.#provider === 'youtube' ? YT_ORIGIN : VIMEO_ORIGIN;
      this.frame?.contentWindow?.postMessage(JSON.stringify(payload), origin);
    }

    #command(action) {
      if (this.#provider === 'youtube') {
        this.#post({ event: 'command', func: action === 'play' ? 'playVideo' : 'pauseVideo', args: [] });
      } else {
        this.#post({ method: action });
      }
    }

    #onFrameMessage(event) {
      let data;

      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }

      if (!data || typeof data !== 'object') return;

      if (this.#provider === 'youtube') {
        const info = data.info;
        if (!info) return;

        if (typeof info.duration === 'number' && info.duration > 0) this.#duration = info.duration;
        if (typeof info.currentTime === 'number') this.#currentTime = info.currentTime;
        if (typeof info.muted === 'boolean') {
          this.#isMuted = info.muted;
          this.#syncMuteButtons();
        }
        // 1 = reproduciendo, 3 = cargando
        if (typeof info.playerState === 'number') {
          this.#setPlaying(info.playerState === 1 || info.playerState === 3);
        }

        this.#renderProgress();
        return;
      }

      const payload = data.data || {};

      switch (data.event) {
        case 'timeupdate':
          this.#duration = payload.duration || this.#duration;
          this.#currentTime = payload.seconds || 0;
          this.#renderProgress();
          break;
        case 'loaded':
          this.#post({ method: 'getDuration' });
          break;
        case 'play':
          this.#setPlaying(true);
          break;
        case 'pause':
        case 'ended':
          this.#setPlaying(false);
          break;
      }
    }
  }

  if (!window.customElements.get('venner-video-player')) {
    window.customElements.define('venner-video-player', VennerVideoPlayer);
  }
}
